import { Button, Modal, message } from 'antd';
import { css, cx } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import useModal from 'hooks/useModal';
import { isNil, noop, pick } from 'lodash-es';
import SMSVerifyModal from 'components/SMSVerifyModal';
import { localPhoneParse } from 'utils/phoneHelpers';
import { updateUserInfo } from 'services/api/account';
import { update as updateSubAccount, updatePassword } from 'services/api/assistant.account';
import serverMsgRaw from 'utils/serverMsg';
import { useAuthorization } from 'components/Authorization';
import useSMSVerify from 'components/SMSVerifyModal/useSMSVerify';
import useIsPasswordSet, { useReCheckIsPasswordSet } from 'hooks/business/user/useIsPasswordSet';
import errorToaster from 'utils/errorToaster';
import { OnlyPhoneFormModal, OnlyPhoneFormModalProps } from './OnlyPhoneForm';
import { OpenOnlyAccountFormModal } from './OnlyAccountForm';
import AccountPasswordForm from '../AccountPasswordForm';
import { useLogout } from 'hooks/useLogout';

export interface ComplexModalFormValues
  extends Pick<Assistant.Account, 'account' | 'name' | 'phoneNumber'> {
  id: Assistant.Account['id'];
}

export interface ComplexModalFormProps {
  initialValues: ComplexModalFormValues;
  onUpdated?: (values: ComplexModalFormValues) => void;
}

const Tag = styled.span`
  background-color: var(--fett-border-default);
  color: var(--fett-text-tertiary);
  padding: 0 8px;
  line-height: 1.8;
  display: inline-block;
  border-radius: 2px;
`;

const ComplexModalForm = (props: ComplexModalFormProps) => {
  const { initialValues, onUpdated = noop } = props;
  const { t } = useTranslate();
  const verifyProps = useSMSVerify();
  const isPwdSet = useIsPasswordSet();
  const showValues = initialValues;
  const { payload, authorize } = useAuthorization<Oversea.Token.Payload>();
  const {
    captchaProps: { coolingdown, onSendSMS: sendVerifySMS },
    encodedPhone,
    verify: doVerifySMS,
  } = verifyProps;
  const smsVerifyModalInst = useModal();
  const passwordModalInst = useModal();
  const onlyAccountModalInst = useModal();
  const onlyPhoneModalInst = useModal<OnlyPhoneFormModalProps>();
  const logout = useLogout();

  const serverMsg = useCallback(
    (...p: Parameters<typeof serverMsgRaw>) =>
      serverMsgRaw(...p)
        .then(message.error)
        .catch((e) => {
          errorToaster(e, t('translation.操作失败，请稍后再试'));
        }),
    [t],
  );
  const isSelf = !isNil(payload?.id) && `${showValues?.id}` === `${payload?.id}`;
  const phoneNumber = showValues?.phoneNumber;
  const account = showValues?.account;
  const openSelfPhoneSettingMoldal = () => {
    // const phoneNumber = initialValues?.phoneNumber;
    const phonedata = localPhoneParse(phoneNumber);
    onlyPhoneModalInst.show({
      formProps: {
        mode: 'renew',
        initialValues: {
          mobile: phonedata?.phone ? '' : undefined,
          countryCode: phonedata?.code || '+86',
        },
        onFinish: (values) => {
          const newPhone = values.countryCode + values.mobile;
          updateUserInfo({
            newPhone,
            smsCode: values.captcha,
          })
            .then((rsp) => {
              const { token } = rsp.data.data;
              authorize(token);
              onUpdated({ phoneNumber: newPhone });
              onlyPhoneModalInst.hide();
              message.success(t('modals.设置成功'));
            })
            .catch((e) => {
              serverMsg(e);
            });
        },
      },
    });
  };
  const pwdRecheck = useReCheckIsPasswordSet();
  useEffect(() => {
    if (!isSelf) {
      return;
    }
    pwdRecheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className="flex items-center">
        <div className="flex items-center">
          <div
            className={cx('text-[var(--fett-text-secondary)]', {
              'mr-[8px]': isSelf,
            })}
          >
            {t('field.password')}:
          </div>
          {isSelf && (
            <div>
              {!isPwdSet ? <Tag>{t('modals.未设置')}</Tag> : <Tag>{t('modals.已设置')}</Tag>}
            </div>
          )}
        </div>
        <Button
          type="link"
          onClick={() => {
            passwordModalInst.show(showValues);
          }}
        >
          {!isSelf || isPwdSet ? t('modals.修改帐号密码') : t('modals.设置帐号密码')}
        </Button>
      </div>
      <div className="flex items-center">
        <div className="flex items-center">
          <div className="mr-[8px] text-[var(--fett-text-secondary)]">{t('field.account')}:</div>
          <div className="text-[var(--fett-text-primary)]">
            {account || <Tag>{t('modals.未设置')}</Tag>}
          </div>
        </div>
        <div>
          <OpenOnlyAccountFormModal
            visible={onlyAccountModalInst.visible}
            onCancel={() => onlyAccountModalInst.hide()}
            onSubmit={(account) => {
              return updateUserInfo({ account })
                .then((rsp) => {
                  const { token } = rsp.data.data;
                  authorize(token);
                  onlyAccountModalInst.hide();
                  message.success(t('modals.设置成功'));
                  onUpdated({
                    id: initialValues?.id,
                    account,
                  });
                })
                .catch((e) => {
                  serverMsg(e);
                });
            }}
          />
          {!isSelf && !account ? null : (
            <Button disabled={!!account} onClick={() => onlyAccountModalInst.show()} type="link">
              {account ? t('modals.不支持修改') : t('modals.设置登录帐号')}
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center">
        <div className="flex items-center">
          <div className="mr-[8px] text-[var(--fett-text-secondary)]">{t('field.phone')}:</div>
          <div className="text-[var(--fett-text-primary)]">
            {showValues?.phoneNumber || <Tag>{t('modals.未设置')}</Tag>}
          </div>
        </div>

        <Button
          type="link"
          onClick={() => {
            const initPhoneNumber = showValues?.phoneNumber;
            if (isSelf) {
              if (!initPhoneNumber) {
                // 未绑定手机号直接打开修改窗
                openSelfPhoneSettingMoldal();
                return;
              }
              const modal = Modal.confirm({
                title: '提示',
                content: (
                  <div>
                    {t('modals.change_phone_tips', {
                      phone: encodedPhone,
                    })}
                    {t('modals.修改成功后，需使用新手机号进行登录', {
                      phone: encodedPhone,
                    })}
                  </div>
                ),
                onOk: () => {
                  // 未绑定手机号或验证码发送过但未正常关闭后，直接打开修改窗
                  if (coolingdown) {
                    smsVerifyModalInst.show();
                    return;
                  }
                  sendVerifySMS().then((isSuccessed) => {
                    if (isSuccessed) {
                      modal.destroy();
                      smsVerifyModalInst.show();
                    }
                  });
                },
              });
            } else {
              const phoneData = localPhoneParse(initPhoneNumber);
              const code = phoneData?.code || '+86';

              onlyPhoneModalInst.show({
                formProps: {
                  mode: 'renew',
                  skipCaptcha: true,
                  initialValues: {
                    countryCode: code,
                    mobile: '',
                  },
                  onFinish: (formValues) => {
                    const data = {
                      id: showValues?.id,
                      phoneNumber: `${formValues.countryCode}${formValues.mobile}`,
                    } as any;
                    updateSubAccount(data)
                      .then(() => {
                        onUpdated(data);
                        onlyPhoneModalInst.hide();
                      })
                      .catch(serverMsg);
                  },
                },
              });
            }
          }}
        >
          {phoneNumber ? t('modals.修改手机号') : t('modals.设置手机号')}
        </Button>
      </div>
      <SMSVerifyModal
        title={t('modals.帐号信息')}
        onSubmit={async (captcha) => {
          if (!captcha) {
            return false;
          }
          const isMatch = await doVerifySMS();
          if (!isMatch) {
            return false;
          }
          smsVerifyModalInst.hide();
          openSelfPhoneSettingMoldal();
          return true;
        }}
        onCancel={smsVerifyModalInst.hide}
        visible={smsVerifyModalInst.visible}
        verifyProps={verifyProps}
      />
      <Modal
        destroyOnClose
        title={!isSelf || isPwdSet ? t('modals.修改帐号密码') : t('modals.设置帐号密码')}
        footer={null}
        onCancel={passwordModalInst.hide}
        visible={passwordModalInst.visible}
      >
        {passwordModalInst.visible ? (
          <AccountPasswordForm
            compact
            initialValues={passwordModalInst.meta as any}
            onSubmit={(values) => {
              const { newPassword, oldPassword } = pick(values, ['newPassword', 'oldPassword']);
              if (!isSelf) {
                updatePassword({ id: showValues?.id as string, newPasswd: newPassword })
                  .then(() => {
                    message.success(t('modals.设置成功'));
                    passwordModalInst.hide();
                  })
                  .catch(serverMsg);
                return;
              }
              updateUserInfo({
                newPassword,
                oldPassword,
              })
                .then(() => {
                  passwordModalInst.hide();
                  Modal.info({
                    content: t('account_update_pwd_success_msg'),
                    onOk: () => {
                      logout(false, 'changePassword-n');
                    },
                    keyboard: false,
                  });
                })
                .catch(serverMsg);
            }}
          >
            {(form, formikHelper) => {
              return (
                <>
                  {form}
                  <Button
                    type="primary"
                    disabled={!formikHelper.dirty || !formikHelper.isValid}
                    loading={formikHelper.isSubmitting}
                    className={css`
                      width: 100%;
                      display: block;
                    `}
                    onClick={() => {
                      formikHelper.handleSubmit();
                    }}
                  >
                    {t('modals.确认修改')}
                  </Button>
                </>
              );
            }}
          </AccountPasswordForm>
        ) : null}
      </Modal>
      {onlyPhoneModalInst.meta && (
        <OnlyPhoneFormModal
          visible={onlyPhoneModalInst.visible}
          onCancel={() => onlyPhoneModalInst.hide()}
          {...onlyPhoneModalInst.meta}
        />
      )}
    </>
  );
};
export default ComplexModalForm;
